// `Tabs` + `RouterTabs` + `NavTabs` (design-system spec §3.10) — the one
// implementation of tab semantics, in the three shapes the product needs:
//
//   - `Tabs`      — real ARIA tabs (roving tabindex, arrow keys) for a strip
//                   that drives ONE panel and owns keyboard focus.
//   - `RouterTabs`— a <nav> of router links with `aria-current` for a
//                   URL-backed subnav (the catalogue's own section nav).
//   - `NavTabs`   — a <nav> of BUTTONS with `aria-current`, for a strip that
//                   switches panels WITHOUT a URL. Same register, same
//                   underline; the controls stay plain buttons.
//
// `NavTabs` exists because §3.10 is explicit that Tabs/RouterTabs "survive
// unchanged in semantics … testids and roles untouched": the product-detail
// strip has always exposed BUTTON roles with `aria-current`, and the certified
// M01 walkthroughs read it that way. Promoting it to a tablist would silently
// re-role a certified surface (and turn one walkthrough's `toHaveCount(0)`
// vacuous). Restyling never rewrites a walkthrough — so the register absorbs
// the shape instead of the screens hand-rolling it.
//
// Underline indicator in the accent; horizontal scroll at overflow on a
// desktop. The gate (§4 checks 3a/4) ratchets ad-hoc tab markup toward these.
//
// THE MODULE STRIP KEEPS TO ONE ROW ON A PHONE (UP5, design-system §3.10 as
// amended by the P3-UX leftovers ruling). At 412 px a `RouterTabs` strip used
// to wrap: measured in Chromium with the product's own stylesheets and a
// full-permission reader, five module strips took two rows in English and the
// inventory and production strips took three in Russian (106 px of navigation
// above the first record). Below 769 px the strip now shows the tabs that fit
// on one row and folds the rest, in their order, behind a "More" tab whose
// panel lists them as the same links. The lit tab is never folded: when it
// would be, it takes the last place on the row. Nothing scrolls sideways and
// every module entry stays one tap away, the property the phone bar settled
// on (§3.17). The strip is measured in a layout effect, so the fold is in
// place before the first paint and the strip's box never moves (§5). The
// in-screen `Tabs` and `NavTabs` strips keep wrapping: they switch a panel,
// their walkthroughs press them by name, and a folded `role="tab"` would leave
// its tablist.

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useDesktop } from "./use-desktop";

/** Which tabs a one-row strip shows and which it folds behind "More". */
export interface TabFold {
  /** Indices drawn on the row, in strip order. */
  shown: number[];
  /** Indices behind "More", in strip order. */
  folded: number[];
}

/**
 * The fold for one row (pure). `widths` are the tabs' drawn widths, `room` the
 * row's inner width, `gap` the space between two items and `more` the width
 * of the "More" tab. Every tab shows when they all fit. Otherwise the row
 * keeps the longest leading run that fits beside "More", with the lit tab
 * (`active`, or -1 for none) taking the last place when it would have folded.
 * The lit tab always shows, even alone on a row too narrow for it.
 */
export function foldTabs(
  widths: readonly number[],
  room: number,
  gap: number,
  more: number,
  active: number,
): TabFold {
  const all = widths.map((_, i) => i);
  const span = (idx: readonly number[]): number =>
    idx.reduce((sum, i) => sum + (widths[i] ?? 0), 0) + gap * Math.max(idx.length - 1, 0);
  if (span(all) <= room) return { shown: all, folded: [] };
  const budget = room - more - gap;
  const lit = active >= 0 && active < widths.length ? active : -1;
  let shown: number[] = lit >= 0 ? [lit] : [];
  for (let k = widths.length - 1; k >= 0; k -= 1) {
    const lead = all.slice(0, k);
    const candidate = lit >= k ? [...lead, lit] : lead;
    if (candidate.length > 0 && span(candidate) <= budget) {
      shown = candidate;
      break;
    }
  }
  const keep = new Set(shown);
  return { shown, folded: all.filter((i) => !keep.has(i)) };
}

interface FoldPlan extends TabFold {
  sig: string;
  widths: number[];
  more: number;
  gap: number;
  room: number;
}

/** Room a fold leaves for sub-pixel widths, so a measured fit never wraps. */
const FOLD_SLACK_PX = 1;

/**
 * Measures a strip and keeps it to one row. While `measuring` the caller draws
 * every tab (each marked `data-fold-item`) and a "More" probe (`data-fold-more`);
 * the layout effect reads their widths and sets the fold before the browser
 * paints. A change of tabs, lit tab or language measures again; a change of
 * the row's width alone refolds from the widths already measured. A shown tab
 * whose own width moves measures again too: a label can change after the
 * first measure (a terminology override read from the server, a count badge
 * that arrives later), and the strip would otherwise wrap onto a second row.
 */
function useTabFold(keys: readonly string[], active: number) {
  const navRef = useRef<HTMLElement | null>(null);
  const [plan, setPlan] = useState<FoldPlan | null>(null);
  const lang = typeof document === "undefined" ? "" : document.documentElement.lang;
  const sig = `${keys.join("\n")}\u0000${String(active)}\u0000${lang}`;
  const measuring = plan?.sig !== sig;

  useLayoutEffect(() => {
    if (!measuring) return;
    const nav = navRef.current;
    if (nav === null) return;
    const widths = Array.from(nav.querySelectorAll<HTMLElement>("[data-fold-item]")).map(
      (el) => el.getBoundingClientRect().width,
    );
    const more = nav.querySelector<HTMLElement>("[data-fold-more]")?.getBoundingClientRect().width;
    const gap = Number.parseFloat(getComputedStyle(nav).columnGap);
    const room = nav.clientWidth - FOLD_SLACK_PX;
    const safeGap = Number.isFinite(gap) ? gap : 0;
    setPlan({
      sig,
      widths,
      more: more ?? 0,
      gap: safeGap,
      room,
      ...foldTabs(widths, room, safeGap, more ?? 0, active),
    });
  }, [measuring, sig, active]);

  // The tabs on the row, re-observed whenever the fold changes which they are.
  const shownKey = plan === null || measuring ? "" : plan.shown.join(",");
  useEffect(() => {
    const nav = navRef.current;
    if (nav === null || typeof ResizeObserver !== "function") return undefined;
    const observer = new ResizeObserver(() => {
      setPlan((prev) => {
        if (prev === null) return prev;
        // The row's items are the shown tabs, in order.
        const items = Array.from(nav.querySelectorAll<HTMLElement>("[data-fold-item]"));
        const moved =
          items.length === prev.shown.length &&
          prev.shown.some((tab, at) => {
            const el = items[at];
            const was = prev.widths[tab];
            return (
              el !== undefined &&
              was !== undefined &&
              Math.abs(el.getBoundingClientRect().width - was) > FOLD_SLACK_PX / 2
            );
          });
        if (moved) return { ...prev, sig: "" };
        const room = nav.clientWidth - FOLD_SLACK_PX;
        if (room === prev.room) return prev;
        return { ...prev, room, ...foldTabs(prev.widths, room, prev.gap, prev.more, active) };
      });
    });
    observer.observe(nav);
    for (const item of nav.querySelectorAll<HTMLElement>("[data-fold-item]")) {
      observer.observe(item);
    }
    return () => {
      observer.disconnect();
    };
  }, [active, shownKey]);

  // A self-hosted script font (Devanagari) can arrive after the first
  // measure and widen every label; measure once more when it has.
  useEffect(() => {
    // jsdom has no FontFaceSet; a browser always does.
    const fonts = (document as { fonts?: FontFaceSet }).fonts;
    if (fonts === undefined) return undefined;
    let alive = true;
    void fonts.ready.then(() => {
      if (alive) setPlan((prev) => (prev === null ? prev : { ...prev, sig: "" }));
    });
    return () => {
      alive = false;
    };
  }, []);

  const shown = plan === null || measuring ? keys.map((_, i) => i) : plan.shown;
  const folded = plan === null || measuring ? [] : plan.folded;
  return { navRef, measuring, shown, folded };
}

/** The "More" tab and its panel of folded tabs. A disclosure, like the row
 * menu: the panel holds the folded links in the reading order after the
 * button, and a choice, Escape or a press outside closes it. */
function TabFoldMenu({
  label,
  testId,
  onChoose,
  children,
}: {
  label: string;
  testId: string;
  /** Called with the chosen link or button, before the panel closes. */
  onChoose: (chosen: Element) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLDivElement | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e: PointerEvent): void => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === "Escape") {
        // Capture phase, so an outer layer's bubble listener on the document
        // (a SidePeek's) sees the key as handled and stays open.
        e.preventDefault();
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  return (
    <div className="ds-tabs-more-wrap" ref={root}>
      <button
        type="button"
        ref={button}
        className="ds-tabs-more"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid={testId}
        data-fold-more=""
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        {label}
        <svg viewBox="0 0 10 10" aria-hidden="true" focusable="false">
          <path d="M1.5 3.5 5 7l3.5-3.5" />
        </svg>
      </button>
      {open ? (
        <div
          id={panelId}
          className="ds-tabs-fold"
          data-testid={`${testId}-panel`}
          onClick={(e) => {
            const chosen = (e.target as Element).closest("a[href], button");
            if (chosen === null) return;
            onChoose(chosen);
            setOpen(false);
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export interface TabDef {
  id: string;
  label: ReactNode;
  /** DOM id for the tab control — stable-selector contract for walkthroughs
   * that key on `#vp-tab-steps`-style ids. Additive (spec §3: props may grow). */
  domId?: string;
  "data-testid"?: string;
}

export interface TabsProps {
  tabs: readonly TabDef[];
  active: string;
  onSelect: (id: string) => void;
  /** Accessible name of the tablist. */
  label: string;
  /** `aria-controls` target — the id of the ONE panel the tabs drive. */
  controls?: string;
  /** Extra class alongside `ds-tabs` (stable selector for walkthroughs). */
  className?: string;
  "data-testid"?: string;
}

export function Tabs({ tabs, active, onSelect, label, controls, className, ...rest }: TabsProps) {
  const listRef = useRef<HTMLDivElement | null>(null);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Home" && e.key !== "End")
      return;
    e.preventDefault();
    const idx = tabs.findIndex((t) => t.id === active);
    const next =
      e.key === "ArrowRight"
        ? tabs[(idx + 1) % tabs.length]
        : e.key === "ArrowLeft"
          ? tabs[(idx - 1 + tabs.length) % tabs.length]
          : e.key === "Home"
            ? tabs[0]
            : tabs[tabs.length - 1];
    if (next !== undefined) {
      onSelect(next.id);
      const el = listRef.current?.querySelector<HTMLButtonElement>(`[data-tab-id="${next.id}"]`);
      el?.focus();
    }
  };

  return (
    <div
      className={className !== undefined ? `ds-tabs ${className}` : "ds-tabs"}
      role="tablist"
      aria-label={label}
      ref={listRef}
      onKeyDown={onKeyDown}
      {...rest}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={tab.domId}
          data-tab-id={tab.id}
          data-testid={tab["data-testid"]}
          aria-selected={tab.id === active}
          aria-controls={controls}
          tabIndex={tab.id === active ? 0 : -1}
          onClick={() => {
            onSelect(tab.id);
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export interface RouterTabDef {
  to: string;
  label: ReactNode;
  "data-testid"?: string;
  /** Further paths that light this tab, each with its descendants. The router
   * lights a link only on its own `to` and below it, so one entry that stands
   * for several sibling addresses names the others here (the sales Pipeline
   * entry covers `/sales/opportunities` and `/sales/forecast`). */
  activePaths?: readonly string[];
  /** Lights only on `to` itself, for a tab whose sibling sits below its path. */
  exact?: boolean;
}

/** True when `pathname` is one of `paths` or sits below one of them. A path
 * that merely starts with the same letters (`/sales/forecasting`) is not. */
function onAnyPath(pathname: string, paths: readonly string[]): boolean {
  return paths.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** A tab that also lights on its `activePaths`. It is its own component so
 * that only a strip which names sibling paths subscribes to the location. */
function SiblingLitTab({
  tab,
  activePaths,
  fold,
}: {
  tab: RouterTabDef;
  activePaths: readonly string[];
  fold?: boolean;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <Link
      to={tab.to}
      data-testid={tab["data-testid"]}
      {...(fold === true ? { "data-fold-item": "" } : {})}
      activeProps={{ "aria-current": "page" }}
      inactiveProps={{ "aria-current": onAnyPath(pathname, activePaths) ? "page" : undefined }}
    >
      {tab.label}
    </Link>
  );
}

/** One router tab, lit by the router (and by `activePaths` when it has any). */
function routerTab(tab: RouterTabDef, fold = false): ReactNode {
  return tab.activePaths !== undefined && tab.activePaths.length > 0 ? (
    <SiblingLitTab key={tab.to} tab={tab} activePaths={tab.activePaths} fold={fold} />
  ) : (
    <Link
      key={tab.to}
      to={tab.to}
      data-testid={tab["data-testid"]}
      {...(fold ? { "data-fold-item": "" } : {})}
      {...(tab.exact === true ? { activeOptions: { exact: true } } : {})}
      activeProps={{ "aria-current": "page" }}
    >
      {tab.label}
    </Link>
  );
}

/** Whether the router lights `tab` at `pathname`: its own path and below it
 * (only the path itself when `exact`), or one of its `activePaths`. */
export function routerTabLit(tab: RouterTabDef, pathname: string): boolean {
  if (tab.exact === true ? pathname === tab.to : onAnyPath(pathname, [tab.to])) return true;
  return onAnyPath(pathname, tab.activePaths ?? []);
}

interface RouterTabsProps {
  tabs: readonly RouterTabDef[];
  label: string;
  className?: string;
  "data-testid"?: string;
}

/** URL-backed subnav: links with aria-current, same underline register. Below
 * 769 px it keeps to one row and folds the rest behind "More" (see the head of
 * this file). */
export function RouterTabs(props: RouterTabsProps) {
  const desktop = useDesktop();
  return desktop ? <RouterTabRow {...props} /> : <FoldingRouterTabs {...props} />;
}

function RouterTabRow({ tabs, label, className, ...rest }: RouterTabsProps) {
  return (
    <nav
      className={className !== undefined ? `ds-tabs ${className}` : "ds-tabs"}
      aria-label={label}
      {...rest}
    >
      {tabs.map((tab) => routerTab(tab))}
    </nav>
  );
}

function FoldingRouterTabs({ tabs, label, className, ...rest }: RouterTabsProps) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = tabs.findIndex((tab) => routerTabLit(tab, pathname));
  const { navRef, measuring, shown, folded } = useTabFold(
    tabs.map((tab) => tab.to),
    active,
  );
  const testId = rest["data-testid"] ?? "tabs";
  // A link chosen from the panel unmounts with it, and the strip measures
  // again for the new lit tab, so the More tab it came from is redrawn too.
  // Focus then goes to the chosen tab, which now stands lit on the row, rather
  // than falling to the page.
  const focusHref = useRef<string | null>(null);
  useEffect(() => {
    const href = focusHref.current;
    if (measuring || href === null) return;
    const tab = Array.from(
      navRef.current?.querySelectorAll<HTMLElement>(
        ':scope > [data-fold-item][aria-current="page"]',
      ) ?? [],
    ).find((el) => el.getAttribute("href") === href);
    if (tab === undefined) return;
    focusHref.current = null;
    tab.focus();
  });
  return (
    <nav
      ref={navRef}
      className={`${className !== undefined ? `ds-tabs ${className}` : "ds-tabs"} ds-tabs-one-row`}
      aria-label={label}
      data-folded={folded.length > 0 ? "true" : undefined}
      {...rest}
    >
      {shown.map((i) => {
        const tab = tabs[i];
        return tab === undefined ? null : routerTab(tab, true);
      })}
      {measuring ? (
        // Measured with the "More" tab's own metrics, then gone before paint.
        <button
          type="button"
          className="ds-tabs-more"
          data-fold-more=""
          tabIndex={-1}
          aria-hidden="true"
        >
          {t("nav.more")}
        </button>
      ) : folded.length > 0 ? (
        <TabFoldMenu
          label={t("nav.more")}
          testId={`${testId}-more`}
          onChoose={(chosen) => {
            focusHref.current = chosen.getAttribute("href");
          }}
        >
          {folded.map((i) => {
            const tab = tabs[i];
            return tab === undefined ? null : routerTab(tab);
          })}
        </TabFoldMenu>
      ) : null}
    </nav>
  );
}

export interface NavTabsProps {
  tabs: readonly TabDef[];
  active: string;
  onSelect: (id: string) => void;
  /** Accessible name of the strip. */
  label: string;
  /** Extra class alongside `ds-tabs` (stable selector for walkthroughs). */
  className?: string;
  "data-testid"?: string;
}

/**
 * Panel switcher with no URL behind it: a `<nav>` of plain BUTTONS carrying
 * `aria-current="page"`, in the §3.10 underline register. Buttons are natively
 * tab-reachable, so there is no roving tabindex to get wrong; the strip drives
 * whatever the caller renders below it.
 */
export function NavTabs({ tabs, active, onSelect, label, className, ...rest }: NavTabsProps) {
  return (
    <nav
      className={className !== undefined ? `ds-tabs ${className}` : "ds-tabs"}
      aria-label={label}
      {...rest}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          id={tab.domId}
          data-tab-id={tab.id}
          data-testid={tab["data-testid"]}
          aria-current={tab.id === active ? "page" : undefined}
          onClick={() => {
            onSelect(tab.id);
          }}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}
